import { Button, Icon24ArrowRight, Typography } from "@cur8/maneki";
import { useLocation } from "@pomle/react-router-paths";
import { useCounterSignConsentMutation } from "hooks/mutations/useCounterSignConsentMutation";
import { useLatestConsentsQuery } from "hooks/queries/usePatientConsentsQuery";
import { useVisitQuery } from "hooks/queries/useVisitQuery";
import { getSafeReturnPath } from "lib/getSafeReturnPath";
import { CheckInParams } from "lib/types/checkInParameters";
import { useCallback, useMemo } from "react";
import { useReporting } from "render/hooks/useReporting";
import { paths } from "render/routes/paths";
import { ActionsFooter } from "render/ui/layout/ActionsFooter";
import { InformationLayout } from "render/ui/layout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import { ChecklistItem } from "render/ui/presentation/ChecklistItem";
import { List } from "render/ui/presentation/List";
import { BailView } from "render/views/BailView";
import styles from "./styles.module.sass";

type CounterSignViewProps = {
  goTo(url: string): void;
  onBack(): void;
} & CheckInParams;

export function StudyCounterSignView({
  goTo,
  onBack,
  patientId,
  visitId,
}: CounterSignViewProps) {
  const { logError } = useReporting();

  const counterSignConsent = useCounterSignConsentMutation();

  const { data: consents } = useLatestConsentsQuery({
    patientId: patientId,
  });

  const { data: visit } = useVisitQuery({ patientId, visitId });

  const location = useLocation();

  const url = useMemo(() => {
    const fallback = visit?.extendedBloodPanel
      ? paths.extendedVisitLimsBloodDrawConfirm.url({ patientId, visitId })
      : paths.assignToRoom.url({ patientId, visitId });
    return getSafeReturnPath(location.searchParams.get("returnPath"), fallback);
  }, [location.searchParams, patientId, visit?.extendedBloodPanel, visitId]);

  const goNext = useCallback(() => {
    goTo(url);
  }, [goTo, url]);

  const handleCounterSignAndFinishCheckIn = async () => {
    if (!consents) {
      return;
    }

    try {
      await Promise.all(
        consents
          .filter((c) => c.relevantSignature.counterSignatureRequired)
          .filter(
            (c) =>
              c.relevantSignature.revoked == false &&
              c.relevantSignature.expired == false &&
              c.relevantSignature.optedOut == false
          )
          .map((consent) =>
            counterSignConsent.mutateAsync({
              consent,
              patientId: patientId,
            })
          )
      );
      goNext();
    } catch (error) {
      logError(error);
    }
  };

  if (!consents || !visit) {
    return <BailView title="No Consents Found" />;
  }

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <div className={styles.layout}>
            <Typography variant="display-s">
              By continuing you confirm and sign that the member...
            </Typography>
            <List>
              <ChecklistItem checked>
                Is able to understand the study information
              </ChecklistItem>
              <List.Divider />
              <ChecklistItem checked>
                Is able to provide an informed consent
              </ChecklistItem>
              <List.Divider />
              <ChecklistItem checked>
                Meets all clinical study requirements
              </ChecklistItem>
              <List.Divider />
              <ChecklistItem checked>
                Has no unanswered questions about the studies
              </ChecklistItem>
            </List>
            <Typography variant="body-m" color="subtle">
              If the member doesn’t meet the requirements, please cancel and
              refund the appointment.
            </Typography>
          </div>
        }
        buttons={
          <ActionsFooter
            left={
              <Button variant="secondary" onPress={onBack} fillContainer>
                Back
              </Button>
            }
            right={
              <Button
                state={counterSignConsent.isPending ? "loading" : undefined}
                onPress={handleCounterSignAndFinishCheckIn}
                fillContainer
                icon={<Icon24ArrowRight />}
              >
                Continue
              </Button>
            }
          />
        }
      />
    </PageFrameContent>
  );
}
